import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import BrowserOnly from 'components/consumer/BrowserOnly';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import JBImage from 'components/essentials/JBImage';
import PDPDesktopFullScreenGallery2 from 'components/consumer/PDP/PDPDesktopFullScreenGallery2';
import PDPGalleryLabelsAndViewingCount from 'components/consumer/PDP/PDPGalleryLabelsAndViewingCount';
import PDPDecideLaterOverlay from 'components/consumer/PDP/PDPDecideLaterOverlay';
import PDPCylindo3dViewer from 'components/consumer/PDP/PDPCylindo3dViewer';
import Placeholder from 'components/consumer/Placeholder';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import { markLuxPageNavigationEnd } from 'commons/lux';
import useScreen from 'hooks/useScreen';
import getImageUrlWithoutQuery from 'commons/getImageUrlWithoutQuery';
import convertImgixPropsToQueryParams from 'commons/convertImgixPropsToQueryParams';
import BarbieLogo from 'assets/images/made-for-barbie-logo.png';
import BipocLogo from 'assets/images/bipoc-badge.png';
import useIsBrowser from 'hooks/useIsBrowser';
import useBrokenImageUrls from 'global-state/brokenImageUrls/useBrokenImageUrls';
import PDPDesktopThumbnails from './PDPDesktopThumbnails';
import { getMainImageImgixPropsForPhoto } from './helpers/imgix-props';
import usePDPDesktopHeroSizeProps from './helpers/usePDPDesktopHeroSizeProps';
import PDPProductIntroVideo from './PDPProductIntroVideo';
import { PDP_MAIN_IMAGE } from './specs/testIds';
import LinkOrFragment from '../../LinkOrFragment';
import PDPDimensionShotViewer from '../PDPDimensionShotViewer';
const QUICKVIEW_GALLERY_TABLET_IMAGE_WIDTH = '100%';
const QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH = 570;

// Cap the SSR/first-paint main image width. The server renders a plain <img>
// (no srcset), and React Float preloads that exact URL as the LCP candidate.
// Without a width, imgix serves the source at native resolution, so an oversized
// source (e.g. the bondi-outdoor-double-sofa config image at 7366px / ~8MB)
// becomes a multi-second LCP. 1600px matches the studio hero default width and
// covers retina; the client react-imgix render upgrades via srcset after hydration.
const SSR_MAIN_IMAGE_MAX_WIDTH = 1600;
const dazeyLogo = 'https://joybird2.imgix.net/user-uploads-staging/JBxDaniDazey-Logo-Button.png';
const PDPDesktopHeroGalleryUI = t0 => {
  const $ = _c(161);
  const {
    campaign,
    configurationPhotos: t1,
    cylindoFeatures,
    dimensionsImage,
    enableCrops: t2,
    introVideoIds,
    isConfigFlipped: t3,
    isCylindoAvailable,
    isCylindoConfigurationPhotos,
    isEditOrder: t4,
    isVideoSelected,
    loading: t5,
    mainPhotoLink,
    mode: t6,
    pdpIsModularBYOProduct: t7,
    productName,
    selectedPhoto,
    setIsVideoSelected,
    setSelectedPhoto,
    showDecideLaterOverlay,
    studioPhotos: t8,
    viewGalleryImageEvent,
    dimensionShotUrls: t9,
    currentDimensionShot,
    hasMultipleDimensionShots: t10,
    selectedDimensionShotIndex: t11,
    goToNextDimensionShot,
    goToPreviousDimensionShot,
    shouldShowDimensionThumbnail: t12
  } = t0;
  let t13;
  if ($[0] !== t1) {
    t13 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t13;
  } else {
    t13 = $[1];
  }
  const configurationPhotos = t13;
  const enableCrops = t2 === undefined ? true : t2;
  const isConfigFlipped = t3 === undefined ? false : t3;
  const isEditOrder = t4 === undefined ? true : t4;
  const loading = t5 === undefined ? false : t5;
  const mode = t6 === undefined ? "pdp" : t6;
  const pdpIsModularBYOProduct = t7 === undefined ? false : t7;
  let t14;
  if ($[2] !== t8) {
    t14 = t8 === undefined ? [] : t8;
    $[2] = t8;
    $[3] = t14;
  } else {
    t14 = $[3];
  }
  const studioPhotos = t14;
  let t15;
  if ($[4] !== t9) {
    t15 = t9 === undefined ? [] : t9;
    $[4] = t9;
    $[5] = t15;
  } else {
    t15 = $[5];
  }
  const dimensionShotUrls = t15;
  const hasMultipleDimensionShots = t10 === undefined ? false : t10;
  const selectedDimensionShotIndex = t11 === undefined ? 0 : t11;
  const shouldShowDimensionThumbnail = t12 === undefined ? false : t12;
  const {
    isMobile,
    isMobileOrTablet,
    isDesktop
  } = useScreen();
  const isQuickviewMode = mode === "quickview";
  const isPdpMode = mode === "pdp";
  const isBrowser = useIsBrowser();
  const [isFullScreenGalleryOpen, setIsFullScreenGalleryOpen] = useState(false);
  const brokenImageUrls = useBrokenImageUrls();
  const isStudioGallery = selectedPhoto?.type === "studio";
  const isDimensionImg = selectedPhoto?.type === "dimensional";
  const isConfigGallery = selectedPhoto?.type === "configuration" || selectedPhoto?.type === "cylindo";
  const isBuildYourOwnImg = selectedPhoto?.type === "buildyourown";
  let t16;
  if ($[6] !== configurationPhotos || $[7] !== isBuildYourOwnImg || $[8] !== isStudioGallery || $[9] !== pdpIsModularBYOProduct || $[10] !== selectedPhoto || $[11] !== studioPhotos) {
    t16 = isStudioGallery ? studioPhotos : pdpIsModularBYOProduct && isBuildYourOwnImg ? [selectedPhoto] : configurationPhotos;
    $[6] = configurationPhotos;
    $[7] = isBuildYourOwnImg;
    $[8] = isStudioGallery;
    $[9] = pdpIsModularBYOProduct;
    $[10] = selectedPhoto;
    $[11] = studioPhotos;
    $[12] = t16;
  } else {
    t16 = $[12];
  }
  const allPhotos = t16;
  let t17;
  if ($[13] !== allPhotos || $[14] !== brokenImageUrls) {
    let t18;
    if ($[16] !== brokenImageUrls) {
      t18 = t19 => {
        const {
          src
        } = t19;
        const cleanImageUrl = getImageUrlWithoutQuery(src);
        return !brokenImageUrls.includes(cleanImageUrl);
      };
      $[16] = brokenImageUrls;
      $[17] = t18;
    } else {
      t18 = $[17];
    }
    t17 = allPhotos.filter(t18);
    $[13] = allPhotos;
    $[14] = brokenImageUrls;
    $[15] = t17;
  } else {
    t17 = $[15];
  }
  const validPhotos = t17;
  const photos = validPhotos;
  let t18;
  if ($[18] !== photos || $[19] !== selectedPhoto?.src) {
    let t19;
    if ($[21] !== selectedPhoto?.src) {
      t19 = t20 => {
        const {
          src: src_0
        } = t20;
        return src_0 === selectedPhoto?.src;
      };
      $[21] = selectedPhoto?.src;
      $[22] = t19;
    } else {
      t19 = $[22];
    }
    t18 = photos.findIndex(t19);
    $[18] = photos;
    $[19] = selectedPhoto?.src;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  const selectedPhotoIndex = Math.max(0, t18);
  const mainPhotoSizeProps = usePDPDesktopHeroSizeProps();
  let t19;
  bb0: {
    if (!selectedPhoto?.src) {
      t19 = null;
      break bb0;
    }
    let t20;
    if ($[23] !== enableCrops || $[24] !== isMobileOrTablet || $[25] !== mainPhotoSizeProps || $[26] !== selectedPhoto) {
      t20 = convertImgixPropsToQueryParams({
        width: SSR_MAIN_IMAGE_MAX_WIDTH,
        ...getMainImageImgixPropsForPhoto(selectedPhoto, enableCrops),
        ...mainPhotoSizeProps
      }, selectedPhoto.src, isMobileOrTablet);
      $[23] = enableCrops;
      $[24] = isMobileOrTablet;
      $[25] = mainPhotoSizeProps;
      $[26] = selectedPhoto;
      $[27] = t20;
    } else {
      t20 = $[27];
    }
    t19 = t20;
  }
  const ssrMainImageSrc = t19;
  const introVideoId = introVideoIds && (isMobile ? introVideoIds.mobile : introVideoIds.desktop);
  const ratioForCroppedImages = isMobileOrTablet ? 1 : 0.6666666666666666;
  const ratio = loading || enableCrops ? ratioForCroppedImages : 0.5;
  const spaceAround = 194;
  let t20;
  bb1: {
    if (isQuickviewMode) {
      t20 = QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH;
      break bb1;
    }
    t20 = `calc((100vh - ${194}px) / ${ratio})`;
  }
  const maxGalleryWidth = t20;
  let t21;
  bb2: {
    if (isQuickviewMode && !isDesktop) {
      t21 = QUICKVIEW_GALLERY_TABLET_IMAGE_WIDTH;
      break bb2;
    }
    if (isQuickviewMode) {
      t21 = QUICKVIEW_GALLERY_MAX_IMAGE_WIDTH;
      break bb2;
    }
    t21 = undefined;
  }
  const galleryWidth = t21;
  let t22;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      setIsFullScreenGalleryOpen(true);
    };
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  const openGalleryModal = t22;
  let t23;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => {
      setIsFullScreenGalleryOpen(false);
    };
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  const closeGalleryModal = t23;
  const isMainPhotoLinked = isQuickviewMode && mainPhotoLink;
  let t24;
  if ($[30] !== galleryWidth || $[31] !== maxGalleryWidth) {
    t24 = {
      maxWidth: maxGalleryWidth,
      width: galleryWidth
    };
    $[30] = galleryWidth;
    $[31] = maxGalleryWidth;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  const t25 = isMainPhotoLinked ? "block focus-visible:outline-offset-4" : undefined;
  const t26 = isMainPhotoLinked ? mainPhotoLink : undefined;
  const t27 = mode === "pdp";
  const t28 = mode === "quickview";
  let t29;
  if ($[33] !== brokenImageUrls || $[34] !== selectedPhoto?.src || $[35] !== t27 || $[36] !== t28) {
    t29 = classnames("bg-transparent border-0 focus:outline-0", {
      "PDPDesktopHeroGalleryUI__MainPhoto--mode-pdp": t27,
      "PDPDesktopHeroGalleryUI__MainPhoto--mode-quickview": t28,
      "flex justify-center": brokenImageUrls.includes(selectedPhoto?.src)
    });
    $[33] = brokenImageUrls;
    $[34] = selectedPhoto?.src;
    $[35] = t27;
    $[36] = t28;
    $[37] = t29;
  } else {
    t29 = $[37];
  }
  let t30;
  if ($[38] !== isConfigGallery || $[39] !== isCylindoConfigurationPhotos || $[40] !== isPdpMode || $[41] !== isStudioGallery || $[42] !== isVideoSelected) {
    t30 = () => {
      if (isPdpMode && (!isCylindoConfigurationPhotos && isConfigGallery || isStudioGallery) && !isVideoSelected) {
        openGalleryModal();
      }
    };
    $[38] = isConfigGallery;
    $[39] = isCylindoConfigurationPhotos;
    $[40] = isPdpMode;
    $[41] = isStudioGallery;
    $[42] = isVideoSelected;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== currentDimensionShot || $[45] !== dimensionShotUrls || $[46] !== enableCrops || $[47] !== goToNextDimensionShot || $[48] !== goToPreviousDimensionShot || $[49] !== hasMultipleDimensionShots || $[50] !== isBrowser || $[51] !== isConfigFlipped || $[52] !== isCylindoAvailable || $[53] !== isCylindoConfigurationPhotos || $[54] !== isDimensionImg || $[55] !== isStudioGallery || $[56] !== isVideoSelected || $[57] !== loading || $[58] !== mainPhotoSizeProps || $[59] !== mode || $[60] !== productName || $[61] !== selectedDimensionShotIndex || $[62] !== selectedPhoto || $[63] !== ssrMainImageSrc) {
    t31 = !loading && (isCylindoAvailable && isCylindoConfigurationPhotos ? isStudioGallery || isDimensionImg : true) && !isVideoSelected && !!selectedPhoto && (!!selectedPhoto.src || isDimensionImg) && (isDimensionImg ? <PDPDimensionShotViewer currentDimensionShot={currentDimensionShot} hasMultipleDimensionShots={hasMultipleDimensionShots} selectedDimensionShotIndex={selectedDimensionShotIndex} totalDimensionShots={dimensionShotUrls.length} onPrevious={goToPreviousDimensionShot} onNext={goToNextDimensionShot} className="h-full w-full" {...getMainImageImgixPropsForPhoto(selectedPhoto, enableCrops)} {...mainPhotoSizeProps} data-testid={PDP_MAIN_IMAGE} lazy={false} priority={true} onLoad={markLuxPageNavigationEnd} /> : !isBrowser && selectedPhoto?.src && !isDimensionImg ? <img className={classnames("h-full w-full object-contain object-center select-none", {
      "p-8": !isStudioGallery && !enableCrops,
      "scale-x-[-1]": !isStudioGallery && isConfigFlipped && !isDimensionImg,
      "hover:cursor-[unset]": !isStudioGallery && isCylindoConfigurationPhotos,
      "hover:cursor-zoom-in": mode === "pdp"
    })} src={ssrMainImageSrc || selectedPhoto.src} alt={productName ? String(productName) : ""} width={1600} height={2400} loading="eager" fetchPriority="high" decoding="async" data-testid={PDP_MAIN_IMAGE} onLoad={markLuxPageNavigationEnd} /> : <JBProductImage className={classnames("h-full w-full object-contain object-center select-none", {
      "p-8": !isStudioGallery && !enableCrops,
      "scale-x-[-1]": !isStudioGallery && isConfigFlipped && !isDimensionImg,
      "hover:cursor-[unset]": !isStudioGallery && isCylindoConfigurationPhotos,
      "hover:cursor-zoom-in": mode === "pdp"
    })} {...getMainImageImgixPropsForPhoto(selectedPhoto, enableCrops)} {...mainPhotoSizeProps} data-testid={PDP_MAIN_IMAGE} lazy={false} priority={true} onLoad={markLuxPageNavigationEnd} />);
    $[44] = currentDimensionShot;
    $[45] = dimensionShotUrls;
    $[46] = enableCrops;
    $[47] = goToNextDimensionShot;
    $[48] = goToPreviousDimensionShot;
    $[49] = hasMultipleDimensionShots;
    $[50] = isBrowser;
    $[51] = isConfigFlipped;
    $[52] = isCylindoAvailable;
    $[53] = isCylindoConfigurationPhotos;
    $[54] = isDimensionImg;
    $[55] = isStudioGallery;
    $[56] = isVideoSelected;
    $[57] = loading;
    $[58] = mainPhotoSizeProps;
    $[59] = mode;
    $[60] = productName;
    $[61] = selectedDimensionShotIndex;
    $[62] = selectedPhoto;
    $[63] = ssrMainImageSrc;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== introVideoId || $[66] !== isVideoSelected || $[67] !== loading) {
    t32 = !loading && isVideoSelected && introVideoId && <PDPProductIntroVideo videoId={introVideoId} />;
    $[65] = introVideoId;
    $[66] = isVideoSelected;
    $[67] = loading;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== cylindoFeatures || $[70] !== isConfigGallery || $[71] !== isCylindoAvailable || $[72] !== isCylindoConfigurationPhotos || $[73] !== isDimensionImg || $[74] !== isStudioGallery || $[75] !== isVideoSelected || $[76] !== loading || $[77] !== selectedPhotoIndex) {
    t33 = !loading && isCylindoAvailable && <div className={classnames("w-full h-full [&_.mxt-btn-download]:hidden [&_.mxt-btn-expand]:hidden", {
      invisible: !(isCylindoAvailable && isCylindoConfigurationPhotos) || isStudioGallery || isDimensionImg || isVideoSelected
    })}><PDPCylindo3dViewer cylindoFeatures={cylindoFeatures} rotateIndex={isConfigGallery ? selectedPhotoIndex : -1} /></div>;
    $[69] = cylindoFeatures;
    $[70] = isConfigGallery;
    $[71] = isCylindoAvailable;
    $[72] = isCylindoConfigurationPhotos;
    $[73] = isDimensionImg;
    $[74] = isStudioGallery;
    $[75] = isVideoSelected;
    $[76] = loading;
    $[77] = selectedPhotoIndex;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== isStudioGallery || $[80] !== isVideoSelected || $[81] !== loading || $[82] !== showDecideLaterOverlay) {
    t34 = !loading && showDecideLaterOverlay && !(isStudioGallery || isVideoSelected) && <PDPDecideLaterOverlay />;
    $[79] = isStudioGallery;
    $[80] = isVideoSelected;
    $[81] = loading;
    $[82] = showDecideLaterOverlay;
    $[83] = t34;
  } else {
    t34 = $[83];
  }
  let t35;
  if ($[84] !== loading) {
    t35 = loading && <Placeholder type="rect" loading={true} />;
    $[84] = loading;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] !== ratio || $[87] !== t29 || $[88] !== t30 || $[89] !== t31 || $[90] !== t32 || $[91] !== t33 || $[92] !== t34 || $[93] !== t35) {
    t36 = <RatioContainer className={t29} ratio={ratio} onClick={t30}>{t31}{t32}{t33}{t34}{t35}</RatioContainer>;
    $[86] = ratio;
    $[87] = t29;
    $[88] = t30;
    $[89] = t31;
    $[90] = t32;
    $[91] = t33;
    $[92] = t34;
    $[93] = t35;
    $[94] = t36;
  } else {
    t36 = $[94];
  }
  let t37;
  if ($[95] !== t25 || $[96] !== t26 || $[97] !== t36) {
    t37 = <LinkOrFragment className={t25} to={t26}>{t36}</LinkOrFragment>;
    $[95] = t25;
    $[96] = t26;
    $[97] = t36;
    $[98] = t37;
  } else {
    t37 = $[98];
  }
  let t38;
  if ($[99] !== isBuildYourOwnImg || $[100] !== isStudioGallery || $[101] !== loading || $[102] !== pdpIsModularBYOProduct) {
    t38 = !loading && pdpIsModularBYOProduct && !isStudioGallery && !isBuildYourOwnImg ? <div className="text-sm font-bold bg-[rgba(44,44,44,.7)] text-white left-[calc(50%_-_85px)] opacity-60 absolute text-center top-[40%] w-[190px] z-[2]">Sample configuration shown</div> : null;
    $[99] = isBuildYourOwnImg;
    $[100] = isStudioGallery;
    $[101] = loading;
    $[102] = pdpIsModularBYOProduct;
    $[103] = t38;
  } else {
    t38 = $[103];
  }
  let t39;
  if ($[104] !== campaign) {
    t39 = campaign === "barbie" && <img src={BarbieLogo} alt="barbie-logo" className="w-20 h-20 absolute top-12 left-4 z-50" />;
    $[104] = campaign;
    $[105] = t39;
  } else {
    t39 = $[105];
  }
  let t40;
  if ($[106] !== campaign) {
    t40 = campaign === "bipoc" && <img src={BipocLogo} alt="bipoc-owned-logo" className="w-20 h-20 absolute top-12 left-4 z-50" />;
    $[106] = campaign;
    $[107] = t40;
  } else {
    t40 = $[107];
  }
  let t41;
  if ($[108] !== campaign) {
    t41 = campaign === "dazey" && <JBImage src={dazeyLogo} className="absolute top-10 left-2 z-50" width={120} />;
    $[108] = campaign;
    $[109] = t41;
  } else {
    t41 = $[109];
  }
  const t42 = loading || !isBrowser;
  let t43;
  if ($[110] !== configurationPhotos || $[111] !== dimensionShotUrls || $[112] !== dimensionsImage || $[113] !== enableCrops || $[114] !== introVideoId || $[115] !== isConfigFlipped || $[116] !== isCylindoConfigurationPhotos || $[117] !== isEditOrder || $[118] !== isVideoSelected || $[119] !== mode || $[120] !== pdpIsModularBYOProduct || $[121] !== productName || $[122] !== selectedPhoto || $[123] !== setIsVideoSelected || $[124] !== setSelectedPhoto || $[125] !== shouldShowDimensionThumbnail || $[126] !== studioPhotos || $[127] !== t42 || $[128] !== viewGalleryImageEvent) {
    t43 = <PDPDesktopThumbnails isEditOrder={isEditOrder} configurationPhotos={configurationPhotos} dimensionsImage={dimensionsImage} shouldShowDimensionThumbnail={shouldShowDimensionThumbnail} dimensionShotUrls={dimensionShotUrls} enableCrops={enableCrops} pdpIsModularBYOProduct={pdpIsModularBYOProduct} introVideoId={introVideoId} isConfigFlipped={isConfigFlipped} isCylindoConfigurationPhotos={isCylindoConfigurationPhotos} isVideoSelected={isVideoSelected} loading={t42} mode={mode} openModal={openGalleryModal} productName={productName} selectedPhoto={selectedPhoto} setSelectedPhoto={setSelectedPhoto} setIsVideoSelected={setIsVideoSelected} studioPhotos={studioPhotos} viewGalleryImageEvent={viewGalleryImageEvent} />;
    $[110] = configurationPhotos;
    $[111] = dimensionShotUrls;
    $[112] = dimensionsImage;
    $[113] = enableCrops;
    $[114] = introVideoId;
    $[115] = isConfigFlipped;
    $[116] = isCylindoConfigurationPhotos;
    $[117] = isEditOrder;
    $[118] = isVideoSelected;
    $[119] = mode;
    $[120] = pdpIsModularBYOProduct;
    $[121] = productName;
    $[122] = selectedPhoto;
    $[123] = setIsVideoSelected;
    $[124] = setSelectedPhoto;
    $[125] = shouldShowDimensionThumbnail;
    $[126] = studioPhotos;
    $[127] = t42;
    $[128] = viewGalleryImageEvent;
    $[129] = t43;
  } else {
    t43 = $[129];
  }
  let t44;
  if ($[130] !== isQuickviewMode || $[131] !== loading || $[132] !== mode) {
    t44 = !loading && <div className="flex justify-between left-4 absolute right-4 top-4 z-[2]"><PDPGalleryLabelsAndViewingCount mode={mode} />{!isQuickviewMode && <PDPFavoriteButton />}</div>;
    $[130] = isQuickviewMode;
    $[131] = loading;
    $[132] = mode;
    $[133] = t44;
  } else {
    t44 = $[133];
  }
  let t45;
  if ($[134] !== t24 || $[135] !== t37 || $[136] !== t38 || $[137] !== t39 || $[138] !== t40 || $[139] !== t41 || $[140] !== t43 || $[141] !== t44) {
    t45 = <div className="mb-8 ml-auto mr-auto relative" style={t24}>{t37}{t38}{t39}{t40}{t41}{t43}{t44}</div>;
    $[134] = t24;
    $[135] = t37;
    $[136] = t38;
    $[137] = t39;
    $[138] = t40;
    $[139] = t41;
    $[140] = t43;
    $[141] = t44;
    $[142] = t45;
  } else {
    t45 = $[142];
  }
  const t46 = productName || "";
  let t47;
  if ($[143] !== t46) {
    t47 = t46.split(" ").join("-");
    $[143] = t46;
    $[144] = t47;
  } else {
    t47 = $[144];
  }
  let t48;
  if ($[145] !== introVideoId || $[146] !== isEditOrder || $[147] !== isFullScreenGalleryOpen || $[148] !== isStudioGallery || $[149] !== isVideoSelected || $[150] !== pdpIsModularBYOProduct || $[151] !== photos || $[152] !== productName || $[153] !== selectedPhotoIndex || $[154] !== setIsVideoSelected || $[155] !== setSelectedPhoto || $[156] !== t47) {
    t48 = <BrowserOnly><PDPDesktopFullScreenGallery2 isEditOrder={isEditOrder} isOpen={isFullScreenGalleryOpen} photos={photos} initialSelectedPhotoIndex={selectedPhotoIndex} onClose={closeGalleryModal} isStudioGallery={isStudioGallery} pdpIsModularBYOProduct={pdpIsModularBYOProduct} productName={productName} key={t47} setSelectedPhoto={setSelectedPhoto} introVideoId={introVideoId} isVideoSelected={isVideoSelected} setIsVideoSelected={setIsVideoSelected} /></BrowserOnly>;
    $[145] = introVideoId;
    $[146] = isEditOrder;
    $[147] = isFullScreenGalleryOpen;
    $[148] = isStudioGallery;
    $[149] = isVideoSelected;
    $[150] = pdpIsModularBYOProduct;
    $[151] = photos;
    $[152] = productName;
    $[153] = selectedPhotoIndex;
    $[154] = setIsVideoSelected;
    $[155] = setSelectedPhoto;
    $[156] = t47;
    $[157] = t48;
  } else {
    t48 = $[157];
  }
  let t49;
  if ($[158] !== t45 || $[159] !== t48) {
    t49 = <>{t45}{t48}</>;
    $[158] = t45;
    $[159] = t48;
    $[160] = t49;
  } else {
    t49 = $[160];
  }
  return t49;
};
PDPDesktopHeroGalleryUI.propTypes = {
  configurationPhotos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['buildyourown', 'configuration', 'cylindo', 'dimensional', 'studio'])
  })),
  dimensionsImage: PropTypes.shape({
    id: PropTypes.number.isRequired,
    src: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired
  }),
  isEditOrder: PropTypes.bool,
  enableCrops: PropTypes.bool,
  loading: PropTypes.bool,
  selectedPhoto: PropTypes.shape({
    type: PropTypes.oneOf(['buildyourown', 'configuration', 'cylindo', 'dimensional', 'studio']),
    src: PropTypes.string
  }),
  setSelectedPhoto: PropTypes.func,
  showDecideLaterOverlay: PropTypes.bool,
  studioPhotos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired
  })),
  isConfigFlipped: PropTypes.bool,
  introVideoIds: PropTypes.shape({
    desktop: PropTypes.string,
    mobile: PropTypes.string
  }),
  setIsVideoSelected: PropTypes.func,
  isVideoSelected: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  productName: PropTypes.string,
  isCylindoAvailable: PropTypes.bool,
  cylindoFeatures: PropTypes.arrayOf(PropTypes.string),
  isCylindoConfigurationPhotos: PropTypes.bool,
  viewGalleryImageEvent: PropTypes.func,
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  mainPhotoLink: PropTypes.string,
  campaign: PropTypes.string
};
export default PDPDesktopHeroGalleryUI;